import React, { useState } from 'react';
import { Typography, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { CheckIcon } from '@heroicons/react/20/solid';
import { useAuth } from "../../context/DBContext";
import { createCheckoutSession } from '../../api/stripe';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

const PRICE_IDS = {
  family: {
    monthly: "price_1PogL22MTG9rRx9M1HXjy94d",
    yearly: "price_1PogL22MTG9rRx9MyXiUBQdL"
  },
  business: {
    yearly: "price_1PqE952MTG9rRx9M2CyXUMy6",
    twoYear: "price_1PqECK2MTG9rRx9M7tLeiULJ"
  }
};

const FEATURES = {
  family: [
    "Access to a map of services across the UK",
    "Use your local authority funding to easily book through our system",
    "Communicate directly with providers through our messaging system",
    "Access a range of SEND tools, support aids, and guidance",
    "Leave reviews on products and services",
    "Regular blogs on all things SEND",
    "Webinars and videos on hot topics",
    "Personalised support based on your child's age and needs",
    "Exclusive discounts and offers"
  ],
  business: [
    "Receive and manage bookings through our in-house system",
    "Receive payments directly for your services",
    "Advertise on our map",
    "Communicate directly with parents/carers with ease",
    "Access a range of SEND tools, support aids, and guidance",
    "Regular blogs on all things SEND",
    "Webinars and videos on hot topics",
    "Consultancy services available",
    "Training and workshops"
  ]
};

const AuthPricingPage = () => {
  const { currentUser, userInfo } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const userRole = userInfo?.role || 'family';
  const features = FEATURES[userRole];
  const prices = {
    monthly: userRole === 'family' ? 7 : null,
    yearly: userRole === 'family' ? 70 : 240,
    twoYear: userRole === 'family' ? null : 400
  };

  const sleep = ms => new Promise(r => setTimeout(r, ms));

  const handleCheckoutSession = async (plan) => {
    if (!currentUser) {
      navigate('/signup');
      return;
    }
    setCheckoutLoading(true);
    try {
      const priceId = PRICE_IDS[userRole][plan];
      await createCheckoutSession(currentUser, priceId);
      await sleep(7000);
    } catch (error) {
      // console.error("Error creating checkout session:", error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <>
      {checkoutLoading && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: 'white' }} spin />} />
        </div>
      )}

      <div style={{ paddingTop: '24px' }}>
        <div
          className="container"
          style={{
            textAlign: 'center',
            minHeight: '100vh',
            paddingBottom: '2rem',
          }}
        >
          <Title level={3}>{userRole === 'family' ? 'Family Subscription' : 'Business Subscription'}</Title>
          <Text>Choose your billing period</Text>

          {userRole === 'family' ? (
            <div className="mx-auto mt-6 grid max-w-lg grid-cols-1 items-stretch gap-y-6 sm:mt-8 lg:max-w-4xl lg:grid-cols-2 lg:gap-x-6 lg:gap-y-0">
              {/* Monthly Plan */}
              <div className="rounded-3xl p-6 ring-1 ring-gray-900/10 sm:p-8 lg:p-10 bg-white/60 sm:mx-4 lg:mx-0 lg:rounded-l-3xl lg:rounded-r-none">
                <h3 className="text-blue-600 text-base font-semibold leading-7 text-center">
                  Monthly Plan
                </h3>
                <p className="mt-4 flex items-baseline gap-x-2 justify-center">
                  <span className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900">
                    £{prices.monthly}
                  </span>
                  <span className="text-sm sm:text-base text-gray-500">/mo</span>
                </p>
                <ul role="list" className="mt-6 sm:mt-8 space-y-3 text-sm leading-6 text-gray-600 text-left">
                  {features.map((feature, index) => (
                    <li key={index} className="flex gap-x-3 items-start">
                      <CheckIcon className="h-6 w-5 flex-none text-blue-600 mt-0.5" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleCheckoutSession('monthly')}
                  disabled={checkoutLoading}
                  className="mt-8 block w-full rounded-md bg-blue-600 px-3.5 py-2 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50"
                >
                  Start Monthly Plan
                </button>
              </div>

              {/* Yearly Plan */}
              <div className="rounded-3xl p-6 ring-1 ring-gray-900/10 sm:p-8 lg:p-10 relative bg-gray-900 shadow-2xl lg:rounded-r-3xl lg:rounded-l-none">
                <div className="absolute -top-4 right-4 sm:right-8 rounded-full bg-green-500 px-3 py-1 text-xs sm:text-sm text-white">
                  Most Popular
                </div>
                <h3 className="text-blue-400 text-base font-semibold leading-7 text-center">
                  Yearly Plan
                </h3>
                <p className="mt-4 flex items-baseline gap-x-2 justify-center">
                  <span className="text-4xl sm:text-5xl font-bold tracking-tight text-white">
                    £{prices.yearly}
                  </span>
                  <span className="text-sm sm:text-base text-gray-400">/year</span>
                </p>
                <ul role="list" className="mt-6 sm:mt-8 space-y-3 text-sm leading-6 text-gray-300 text-left">
                  {features.map((feature, index) => (
                    <li key={index} className="flex gap-x-3 items-start">
                      <CheckIcon className="h-6 w-5 flex-none text-blue-400 mt-0.5" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleCheckoutSession('yearly')}
                  disabled={checkoutLoading}
                  className="mt-8 block w-full rounded-md bg-blue-500 px-3.5 py-2 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:opacity-50"
                >
                  Start Yearly Plan
                </button>
              </div>
            </div>
          ) : (
            <div className="mx-auto mt-6 grid max-w-lg grid-cols-1 items-stretch gap-y-6 sm:mt-8 lg:max-w-4xl lg:grid-cols-2 lg:gap-x-6 lg:gap-y-0">
              {/* Yearly Plan */}
              <div className="rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10 bg-white/60 sm:mx-8 lg:mx-0 lg:rounded-l-3xl lg:rounded-r-none">
                <h3 className="text-blue-600 text-base font-semibold leading-7 text-center">
                  Yearly Plan
                </h3>
                <p className="mt-4 flex items-baseline gap-x-2 justify-center">
                  <span className="text-5xl font-bold tracking-tight text-gray-900">
                    £{prices.yearly}
                  </span>
                  <span className="text-base text-gray-500">/year</span>
                </p>
                <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-600 text-left">
                  {features.map((feature, index) => (
                    <li key={index} className="flex gap-x-3 items-start">
                      <CheckIcon className="h-6 w-5 flex-none text-blue-600 mt-0.5" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleCheckoutSession('yearly')}
                  disabled={checkoutLoading}
                  className="mt-8 block w-full rounded-md bg-blue-600 px-3.5 py-2 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50"
                >
                  Start Yearly Plan
                </button>
              </div>

              {/* Two-Year Plan */}
              <div className="rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10 relative bg-gray-900 shadow-2xl lg:rounded-r-3xl lg:rounded-l-none">
                <div className="absolute -top-4 right-8 rounded-full bg-green-500 px-3 py-1 text-sm text-white">
                  Best Value
                </div>
                <h3 className="text-blue-400 text-base font-semibold leading-7 text-center">
                  2-Year Plan
                </h3>
                <p className="mt-4 flex items-baseline gap-x-2 justify-center">
                  <span className="text-5xl font-bold tracking-tight text-white">
                    £{prices.twoYear}
                  </span>
                  <span className="text-base text-gray-400">/2 years</span>
                </p>
                <ul role="list" className="mt-8 space-y-3 text-sm leading-6 text-gray-300 text-left">
                  {features.map((feature, index) => (
                    <li key={index} className="flex gap-x-3 items-start">
                      <CheckIcon className="h-6 w-5 flex-none text-blue-400 mt-0.5" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleCheckoutSession('twoYear')}
                  disabled={checkoutLoading}
                  className="mt-8 block w-full rounded-md bg-blue-500 px-3.5 py-2 text-center text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:opacity-50"
                >
                  Start 2-Year Plan
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AuthPricingPage; 